import type { CssVars } from '../../../css-vars';
import { legacyBorderSideVar } from '../../../features/border/border.css-vars';
import type { AdvancedBorder, AdvancedRadius } from '../../common/schemas/legacy/button-settings';
import type { ThemeButtonDataLegacy, ThemeButtonHoverLegacy } from '../../common/schemas/legacy/theme-button';

export type { CssVars };

/**
 * Shared themed-button CSS-variable builders — the custom colour / border /
 * radius / hover bag every form-family widget paints its buttons with.
 *
 * The `prefix` namespaces the output so one implementation serves the
 * subscription form's submit button (`--form-btn`), its chat-bot button
 * (`--form-chatbot`) and modal button (`--form-modal-btn`), the payment form's
 * buttons, and the login-edu widget's modal triggers (`--login-edu-trigger`)
 * and per-entry buttons (`--login-edu-btn`) — the same shape as the button
 * widget's `getCustomButtonCssVars`.
 *
 * **Colour resolution is injected.** Theme colours are stored as a preset name
 * or a raw CSS colour and are resolved through `@sites/site-theme`, which is an
 * Angular library; entity-core stays framework-free (zod + entity-migrator
 * only), so the host passes its resolver in rather than this package importing
 * one. `apps/websites/.../common/css-vars/css-vars.utils.ts` binds it once.
 */

/**
 * Resolves a theme colour (a preset name or a literal CSS colour, possibly a
 * gradient stop list) to a CSS value — `null` when nothing is set, so the caller
 * omits the variable.
 */
export type ColorCssResolver = (value: string | string[] | undefined) => string | null;

/**
 * The slice of a button's data these builders read. Deliberately structural:
 * the form widget's `FormButtonSettings`, login-edu's trigger and its themed
 * button entries all satisfy it without a shared nominal type.
 */
export type ThemedButtonLike = {
    buttonBase?: ThemeButtonDataLegacy | null;
    buttonHover?: ThemeButtonHoverLegacy | null;
    advancedRadius?: AdvancedRadius | null;
    advancedBorderSettings?: AdvancedBorder | null;
};

export function getThemedButtonCssVars(button: ThemedButtonLike, prefix: string, colorCss: ColorCssResolver): CssVars {
    const base = button.buttonBase;
    const borders = button.advancedBorderSettings;
    const radius = button.advancedRadius;
    const hover = button.buttonHover;

    const vars: CssVars = {
        [`${prefix}-color`]: colorCss(base?.color),
        [`${prefix}-bg`]: colorCss(base?.background),
    };

    if (borders) {
        const top = legacyBorderSideVar(borders, 'top');

        vars[`${prefix}-border-top`] = top;
        vars[`${prefix}-border-right`] = borders.isIndependentBorders ? legacyBorderSideVar(borders, 'right') : top;
        vars[`${prefix}-border-bottom`] = borders.isIndependentBorders ? legacyBorderSideVar(borders, 'bottom') : top;
        vars[`${prefix}-border-left`] = borders.isIndependentBorders ? legacyBorderSideVar(borders, 'left') : top;
    } else {
        vars[`${prefix}-border-color`] = colorCss(base?.borderColor);
        vars[`${prefix}-border-width`] = `${base?.borderWidth}px`;
        vars[`${prefix}-border-style`] = base?.borderStyle ?? null;
    }

    if (radius) {
        const corner = (value: number | null | undefined): string => `${value ?? base?.borderRadius}px`;
        vars[`${prefix}-radius`] = [
            corner(radius.borderTopLeftRadius),
            corner(radius.borderTopRightRadius),
            corner(radius.borderBottomRightRadius),
            corner(radius.borderBottomLeftRadius),
        ].join(' ');
    } else {
        vars[`${prefix}-radius`] = `${base?.borderRadius}px`;
    }

    switch (hover?.hoverType) {
        case 'opacity':
            vars[`${prefix}-hover-opacity`] = `${hover.opacity * 0.01}`;
            break;
        case 'shadow':
            vars[`${prefix}-hover-shadow`] = hover.shadow;
            break;
        case 'color':
            vars[`${prefix}-hover-color`] = colorCss(hover.color);
            vars[`${prefix}-hover-bg`] = colorCss(hover.background);
            vars[`${prefix}-hover-border-color`] = colorCss(hover.borderColor);
            break;
        default:
            break;
    }

    return vars;
}

/**
 * `<prefix>-border-advanced` + `<prefix>-hover-*` modifier classes.
 *
 * These gates matter: an unset custom property makes its declaration invalid at
 * computed-value time, which resets the property to its initial value instead of
 * letting the theme preset's own class show through. The hover kinds get
 * separate classes for the same reason.
 */
export function getThemedButtonHoverClasses(button: ThemedButtonLike, prefix: string): string[] {
    const classes: string[] = [];

    if (button.advancedBorderSettings) {
        classes.push(`${prefix}-border-advanced`);
    }

    switch (button.buttonHover?.hoverType) {
        case 'opacity':
            classes.push(`${prefix}-hover-opacity`);
            break;
        case 'shadow':
            classes.push(`${prefix}-hover-shadow`);
            break;
        case 'color':
            classes.push(`${prefix}-hover-color`);
            break;
        default:
            break;
    }

    return classes;
}
